import { getDisplayMode, Log } from '@utils';

interface IDeferredPrompt extends Event {
	userChoice: Promise<{ outcome: string }>;

	prompt(): void;

	preventDefault(): void;
}

interface IOptions {
	isInstallable?: boolean;
	beforeInstallCallback?(deferredPrompt?: IDeferredPrompt): void;
	afterInstallCallback?(): void;
	onToggleOnlineStatus?(isOnline: boolean): void;
	onInit?(): void;
}

async function isReachable(url: string) {
	/**
	 * Note: fetch() still "succeeds" for 404s on subdirectories,
	 * which is ok when only testing for domain reachability.
	 *
	 * Example:
	 *   https://google.com/noexist does not throw
	 *   https://noexist.com/noexist does throw
	 */
	return fetch(url, { method: 'HEAD', mode: 'no-cors' })
		.then(function(resp) {
			return resp && (resp.ok || resp.type === 'opaque');
		})
		.catch(function(err) {
			Log.message('[conn test failure]:', err);
		});
}

export class PWA {

	deferredPrompt: IDeferredPrompt = null;

	static async isInstalled() {
		const relatedApps = window?.navigator?.getInstalledRelatedApps && (await window.navigator?.getInstalledRelatedApps()) || [];
		let result = false;

		relatedApps.forEach((app: { platform: string; url: string }) => {
			// if PWA exists in the array it is installed
			if (app.platform === 'webapp') {
				result = true;
			}
		});

		return result;
	}

	init({
		isInstallable,
		beforeInstallCallback,
		afterInstallCallback,
		onInit,
		onToggleOnlineStatus,
	}: IOptions) {
		if (isInstallable) {
			window?.addEventListener('beforeinstallprompt', (e: IDeferredPrompt) => {
				// Prevent the mini-infobar from appearing on mobile
				e.preventDefault();

				// Stash the event so it can be triggered later.
				this.deferredPrompt = e;

				// Update UI notify the user they can install the PWA
				beforeInstallCallback && beforeInstallCallback(this.deferredPrompt);
				// Optionally, send analytics event that PWA install promo was shown.
				Log.message(`'beforeinstallprompt' event was fired.`, getDisplayMode());
			});
			window.addEventListener('appinstalled', () => {
				afterInstallCallback && afterInstallCallback();

				// Clear the deferredPrompt so it can be garbage collected
				this.deferredPrompt = null;
				// Optionally, send analytics event to indicate successful install
				Log.message('PWA was installed', getDisplayMode(), PWA.isInstalled());
			});
		}

		onInit && onInit();

		function handleNetworkChange() {
			if (navigator) {
				if (navigator.onLine) {
					void isReachable(window?.location.origin).then(function(online) {
						if (online) {
							onToggleOnlineStatus(true);
						} else {
							onToggleOnlineStatus(false);
						}
					});
				} else {
					onToggleOnlineStatus(false);
				}
			}
		}

		handleNetworkChange();
		window?.addEventListener('online', handleNetworkChange);
		window?.addEventListener('offline', handleNetworkChange);
	}

	async install({
		afterAcceptInstallCallback,
		afterDeclineInstallCallback,
	}: {
		afterAcceptInstallCallback?(): void;
		afterDeclineInstallCallback?(): void;
	}) {
		// Show the install prompt
		const promptEvent = this.deferredPrompt;

		if (!promptEvent) {
			// The deferred prompt isn't available.
			return;
		}

		this.deferredPrompt.prompt();

		// Wait for the user to respond to the prompt
		const { outcome } = await this.deferredPrompt.userChoice;

		// Optionally, send analytics event with outcome of user choice
		Log.message(`User response to the install prompt: ${outcome}`);

		switch (outcome) {
			case 'dissmiss':
				afterDeclineInstallCallback && afterDeclineInstallCallback();
				break;
			case 'accepted':
				afterAcceptInstallCallback && afterAcceptInstallCallback();
				break;
		}

		// We've used the prompt, and can't use it again, throw it away
		this.deferredPrompt = null;
	}

}
